import type { ConfidentialOperationKind } from '@infinex/actions';

/**
 * Ops that may surface in the MAIN app — the boundary moves between the
 * Infinex wallet and the Confidential Wallet. Everything else (private
 * swap/send/transfer and any FUTURE confidential op) belongs only to the
 * confidential app, so the check is an allow-list: unknown ops fail CLOSED
 * (hidden from the main app) rather than leaking in by default.
 */
const MAIN_SURFACE_CONFIDENTIAL_OPS = [
  'deposit',
  'shield',
  'unshield',
] as const satisfies readonly ConfidentialOperationKind[];

/**
 * Whether a swidge order may appear on the main app's surfaces (activity feed,
 * recently-swidged suggestions). Public/legacy rows carry no op and pass.
 * A `withdrawal` passes ONLY when the server stamped it as a move to the
 * user's OWN wallet (`confidentialSelfWithdrawal`) — a boundary move to an
 * imported wallet, surfaced like an unshield; third-party private sends stay
 * confidential-app-only and keep failing closed.
 */
export function isMainSurfaceSwidgeOrder(order: {
  confidentialOperation?: string | null;
  confidentialSelfWithdrawal?: boolean | null;
}): boolean {
  const op = order.confidentialOperation;
  return (
    op == null ||
    (MAIN_SURFACE_CONFIDENTIAL_OPS as readonly string[]).includes(op) ||
    isConfidentialSelfWithdrawal(order)
  );
}

/**
 * Which way a confidential BOUNDARY move crosses, or `null` for everything
 * else (public orders and confidential-internal ops). `in` = funds entering
 * the Confidential Wallet (shield/deposit destined for the confidential side);
 * `out` = funds returning to the user's own wallets — the platform wallet
 * (unshield) or an imported wallet (a server-stamped own-wallet withdrawal).
 * Both sides of a boundary move are the user's OWN accounts — there is no
 * third-party recipient. The single classification the title and detail
 * surfaces share.
 */
export function confidentialBoundaryDirection(order: {
  confidentialOperation?: string | null;
  confidentialSelfWithdrawal?: boolean | null;
  quote: { to: { chain: string } };
}): 'in' | 'out' | null {
  const op = order.confidentialOperation;
  if (
    (op === 'shield' || op === 'deposit') &&
    order.quote.to.chain === 'nearIntents'
  ) {
    return 'in';
  }
  if (op === 'unshield') return 'out';
  if (isConfidentialSelfWithdrawal(order)) return 'out';
  return null;
}

/**
 * Whether an order is a server-stamped move to one of the user's OWN wallets
 * (an own-wallet `withdrawal` — see `confidentialSelfWithdrawal` on the order
 * schema). Distinct from the broader boundary check because this is the ONE
 * boundary shape whose received side is NOT truthfully renderable from the
 * order: the funds exit to the destination wallet's own chain, but the quote's
 * `to` leg still describes the confidential (`nearIntents`) side and its
 * address is the user's intents account — so the received-side Network and
 * Address rows must be suppressed rather than rendered from those fields
 * (they would claim the wrong network and the wrong wallet). An `unshield`
 * does NOT need this: its funds genuinely land on the intents side, so the
 * same rows are accurate for it.
 */
export function isConfidentialSelfWithdrawal(order: {
  confidentialOperation?: string | null;
  confidentialSelfWithdrawal?: boolean | null;
}): boolean {
  return (
    order.confidentialOperation === 'withdrawal' &&
    order.confidentialSelfWithdrawal === true
  );
}
